/**
 * 班级成员 — list, change class-scoped roles, remove, and mint a reusable invite.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Role } from "@xinghai/shared";
import { Button, Field, Icon, Input, Select, Sheet, useConfirmSafe, useToast } from "@xinghai/ui";
import { classesApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";
import { CopyField } from "./common";
import { QrCode } from "./QrCode";
import { ANNOTATOR_TRACK_LABEL, INVITE_TRACKS, ROLE_LABEL } from "./labels";
import type { AnnotatorTrack, ClassItem, Invitation } from "./types";

const CLASS_ROLES: Role[] = ["student", "teacher", "annotator", "adjudicator"];
const INVITE_ROLES: Role[] = ["annotator", "adjudicator", "teacher"];

const EXPIRY = [
  { value: "24", label: "1 天" },
  { value: "168", label: "7 天" },
  { value: "720", label: "30 天（默认）" },
  { value: "8760", label: "1 年" },
];

function usesLabel(inv: Invitation): string {
  const used = inv.use_count ?? 0;
  if (inv.max_uses == null) return `${used}/不限`;
  return `${used}/${inv.max_uses}`;
}

export function MembersSheet({
  classItem,
  selfId,
  canManage,
  onClose,
}: {
  classItem: ClassItem;
  selfId: string | null;
  canManage: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();
  const [roles, setRoles] = useState<Role[]>(["annotator"]);
  const [track, setTrack] = useState<AnnotatorTrack | "">("E1_phonetics");
  const [expiry, setExpiry] = useState("720");
  const [maxUses, setMaxUses] = useState("20");
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState<Invitation | null>(null);

  const members = useQuery({
    queryKey: qk.classMembers(classItem.id),
    queryFn: ({ signal }) => classesApi.members(classItem.id, signal),
  });
  const invites = useQuery({
    queryKey: qk.classInvitations(classItem.id),
    queryFn: ({ signal }) => classesApi.invitations(classItem.id, signal),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.classMembers(classItem.id) });
    void qc.invalidateQueries({ queryKey: qk.classInvitations(classItem.id) });
    void qc.invalidateQueries({ queryKey: qk.classes });
  };

  const saveRoles = useMutation({
    mutationFn: ({ userId, next }: { userId: string; next: Role[] }) =>
      classesApi.setMemberRoles(classItem.id, userId, next),
    onSuccess: () => {
      toast.success("已更新班级角色");
      refresh();
    },
    onError: (e: Error) => toast.error(friendlyMessage(e, "没能改角色")),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => classesApi.removeMember(classItem.id, userId),
    onSuccess: () => {
      toast.success("已移出班级");
      refresh();
    },
    onError: (e: Error) => toast.error(friendlyMessage(e, "没能移出")),
  });

  const create = useMutation({
    mutationFn: () =>
      classesApi.createInvitation(classItem.id, {
        roles,
        ...(roles.includes("annotator") && track ? { annotator_track: track } : {}),
        ...(label.trim() ? { label: label.trim() } : {}),
        max_uses: maxUses === "unlimited" ? null : Number(maxUses),
        expires_in_hours: Number(expiry),
      }),
    onSuccess: (r) => {
      setCreated(r.invitation);
      toast.success("邀请链接已生成");
      refresh();
    },
    onError: (e: Error) => toast.error(friendlyMessage(e, "没能生成邀请")),
  });

  const revoke = useMutation({
    mutationFn: (token: string) => classesApi.revokeInvitation(token),
    onSuccess: () => {
      toast.success("邀请链接已撤销");
      refresh();
    },
    onError: (e: Error) => toast.error(friendlyMessage(e, "没能撤销")),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title={`${classItem.name} 的成员`}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          关闭
        </Button>
      }
    >
      <div className="xz-stack">
        <section className="xz-stack xz-members">
          <h2 className="gz-type-ui">成员</h2>
          <p className="gz-muted">移出只拿掉这个人在本班的身份，不会停用或轮换邀请码。</p>
          {members.isPending ? (
            <p className="gz-muted">正在加载成员…</p>
          ) : members.isError ? (
            <p className="gz-notice gz-notice--error" role="alert">
              成员列表没能加载。
            </p>
          ) : (members.data?.members.length ?? 0) === 0 ? (
            <p className="gz-muted">这个班还没有成员。学生用班级邀请码注册，老师和标注专家用下面的链接加入。</p>
          ) : (
            <ul className="xz-stack">
              {members.data?.members.map((m) => (
                <li key={m.user_id} className="xz-member">
                  <div className="xz-member__main xz-stack">
                  <strong>
                    {m.display_name || m.username}{" "}
                    <span className="gz-caption xz-adm__mono">{m.username}</span>
                  </strong>
                  <span className="xz-adm__tags">
                    {CLASS_ROLES.map((role) => (
                      <label key={role} className="xz-form__choice">
                        <input
                          type="checkbox"
                          checked={m.roles.includes(role)}
                          disabled={!canManage || saveRoles.isPending}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...m.roles, role]
                              : m.roles.filter((r) => r !== role);
                            if (next.length === 0) {
                              toast.error("至少留一个班级角色，或者把这个人移出班级。");
                              return;
                            }
                            saveRoles.mutate({ userId: m.user_id, next });
                          }}
                        />
                        <span>{ROLE_LABEL[role]}</span>
                      </label>
                    ))}
                  </span>
                  </div>
                  {canManage && m.user_id !== selfId && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="xz-member__remove"
                      aria-label={`移出 ${m.display_name || m.username}`}
                      loading={remove.isPending}
                      onClick={async () => {
                        const name = m.display_name || m.username;
                        const ok = await confirm({
                          title: `把 ${name} 移出 ${classItem.name}？`,
                          message: `${name} 会离开这个班，账号本身还在。这和停用、轮换邀请码不是一回事。`,
                          confirmLabel: "移出",
                          danger: true,
                        });
                        if (ok) remove.mutate(m.user_id);
                      }}
                    >
                      移出
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {canManage && (
          <section className="xz-stack xz-members__invites">
            <h2 className="gz-type-ui">邀请老师或标注专家</h2>
            <p className="gz-muted">
              链接可以给多个人用。已经有账号的人登录后点一次就能加入；还没有账号的人用链接自己开号。停用和轮换学生注册用的班级邀请码在班级列表上，不在某一位成员旁边。
            </p>
            {created ? (
              <div className="xz-stack">
                <p className="xz-adm__mono" data-testid="class-invite-code">
                  {created.code ?? created.token}
                </p>
                <CopyField label="邀请链接" value={created.url} />
                <QrCode value={created.url} label="邀请二维码" />
                <Button variant="secondary" onClick={() => setCreated(null)}>
                  再生成一条
                </Button>
              </div>
            ) : (
              <div className="xz-form">
                <fieldset className="xz-form__choices">
                  <legend>邀请身份</legend>
                  {INVITE_ROLES.map((role) => (
                    <label key={role} className="xz-form__choice">
                      <input
                        type="checkbox"
                        checked={roles.includes(role)}
                        onChange={(e) =>
                          setRoles((prev) =>
                            e.target.checked ? [...prev, role] : prev.filter((r) => r !== role),
                          )
                        }
                      />
                      <span>{ROLE_LABEL[role]}</span>
                    </label>
                  ))}
                </fieldset>
                {roles.includes("annotator") && (
                  <Field label="标注方向">
                    <Select
                      value={track}
                      onChange={(e) => setTrack(e.target.value as AnnotatorTrack | "")}
                      options={INVITE_TRACKS.map((t) => ({
                        value: t.value,
                        label: ANNOTATOR_TRACK_LABEL[t.value] ?? t.label,
                      }))}
                    />
                  </Field>
                )}
                <Field label="备注（可选）" hint="只给你自己看，例如「声乐二班标注专家」">
                  <Input value={label} onChange={(e) => setLabel(e.target.value)} />
                </Field>
                <Field label="有效期">
                  <Select
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    options={EXPIRY}
                  />
                </Field>
                <Field label="可用次数">
                  <Select
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    options={[
                      { value: "1", label: "1 次" },
                      { value: "20", label: "20 次（默认）" },
                      { value: "unlimited", label: "不限次数" },
                    ]}
                  />
                </Field>
                <Button
                  disabled={roles.length === 0}
                  loading={create.isPending}
                  onClick={() => create.mutate()}
                >
                  <Icon id="ui-key" size={16} /> 生成链接
                </Button>
              </div>
            )}

            {(invites.data?.invitations.length ?? 0) > 0 && (
              <ul className="xz-stack">
                {invites.data?.invitations.map((inv) => {
                  const code = inv.code ?? inv.token;
                  return (
                  <li key={code} className="xz-stack">
                    <span className="xz-adm__mono">{inv.label || code}</span>
                    <span className="gz-caption">
                      {inv.roles.map((r) => ROLE_LABEL[r] ?? r).join(" · ")} · {usesLabel(inv)} ·{" "}
                      {inv.status === "revoked" || inv.revoked_at
                        ? "已撤销"
                        : inv.status === "expired"
                          ? "已过期"
                          : inv.status === "used"
                            ? "已用完"
                            : "可用"}
                    </span>
                    {!inv.revoked_at && inv.status !== "revoked" && (
                      <Button
                        size="sm"
                        variant="danger"
                        loading={revoke.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "撤销这条邀请链接？",
                            message: "撤销之后已经发出去的链接也立刻失效。用过的记录会留下来。",
                            confirmLabel: "撤销",
                            danger: true,
                          });
                          if (ok) revoke.mutate(code);
                        }}
                      >
                        撤销
                      </Button>
                    )}
                  </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>
    </Sheet>
  );
}
